import type { GeographicMarketTimeSeries as MarketDataTimeSeries } from '@einwert/valuation-api-client'
import {
    Button,
    isoDateStringToLocalFormat,
    joinDefinedStrings,
    MapPinAreaIcon,
    MarketDataChart,
    tokens,
    Typography,
} from 'ComponentLibrary'

import { Box, Divider, GridLegacy as Grid, Link as MuiLink, Stack, styled, useTheme } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useMarketDataAdapter } from 'ClientPortal/src/pages/MarketData/Explorer/MarketDataAdapter.data'
import { MarketDataTable } from 'ClientPortal/src/pages/MarketData/Explorer/MarketDataTable'
import { ArrowsInSimple, ArrowsOutSimple, ArrowSquareOut } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type MarketDataExpandableProps = {
    title: string
    isDemo?: boolean
    series: MarketDataTimeSeries[]
}

export const MarketDataExpandable = ({ title, series, isDemo }: MarketDataExpandableProps) => {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t } = useTranslation('pages', { keyPrefix: 'marketData' })
    const locale = useCurrentResolvedLocale()
    const theme = useTheme()
    const { mapValueTypeToUnit, mapTimeSeriesToChartData } = useMarketDataAdapter()
    const cardRef = useRef<HTMLDivElement>(null)
    const [isExpanded, setIsExpanded] = useState(false)

    // This is not a good idea, but soon we will restructure the backend response which will make our mappings obsolete (or easier).
    const { origin, region, period, value_type: valueType } = series[0]
    const unit = mapValueTypeToUnit(valueType)
    const ExpandIcon = isExpanded ? ArrowsInSimple : ArrowsOutSimple

    useEffect(() => {
        isExpanded && setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400)
    }, [isExpanded])

    return (
        <Grid item xs={12} md={isExpanded ? 12 : 6} ref={cardRef}>
            <StyledCard maxWidth={isExpanded ? 'clamp(580px, 1200px)' : '580px'}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" gap={theme.spacing(2)} alignItems="baseline">
                        <Typography variant="headline3" color={theme.palette.customtext.darkblue100}>
                            {title}
                        </Typography>
                        {unit && (
                            <Typography variant="bodyTextSmall" color={theme.palette.customtext.lightgrey}>
                                {t('unitPrefix', { unit })}
                            </Typography>
                        )}
                    </Stack>
                    <Button
                        variant="secondary"
                        onClick={() => setIsExpanded((prev) => !prev)}
                        startIcon={<ExpandIcon size={20} color={theme.palette.customtext.lightgrey} />}
                    />
                </Stack>
                <Stack direction="row" gap={theme.spacing(1)}>
                    <Stack direction="row" gap={theme.spacing(2)} alignItems="center">
                        <MapPinAreaIcon height="16px" width="16px" color={theme.palette.customtext.lightgrey} />
                        <Typography variant="bodyTextMedium" color={theme.palette.customtext.lightgrey}>
                            {joinDefinedStrings(
                                [isDemo ? t('demoCity') : region.name, isoDateStringToLocalFormat(origin?.date, false, locale)],
                                ' | '
                            )}
                            {' | '}
                        </Typography>
                    </Stack>
                    {origin && (
                        <Link href={origin.link.startsWith('https://') ? origin.link : `https://${origin.link}`} target="blank">
                            <Stack direction="row" gap={theme.spacing(1)} alignItems="center">
                                <Typography variant="bodyTextMedium" color={theme.palette.customtext.lightgrey}>
                                    {origin.title}
                                </Typography>
                                <ArrowSquareOut color={theme.palette.customtext.lightgrey} size={tokens.SizingIconS} weight="bold" />
                            </Stack>
                        </Link>
                    )}
                </Stack>
                <Stack direction="column" height="364px">
                    <MarketDataChart
                        series={mapTimeSeriesToChartData(series)}
                        unit={unit}
                        period={period}
                        fullWidth={isExpanded}
                        emptyStateTitle={tCommon('notEnoughDataAvailableYet')}
                        locale={locale}
                        chartCopy={{ draft: tCommon('draft'), date: tCommon('date'), year: tCommon('year') }}
                    />
                </Stack>
                {isExpanded && (
                    <Stack direction="column" gap={theme.spacing(6)}>
                        <Divider />
                        <MarketDataTable series={series} valueType={valueType} />
                    </Stack>
                )}
            </StyledCard>
        </Grid>
    )
}

const Link = styled(MuiLink)(({ theme }) => ({
    color: theme.palette.customtext.lightgrey,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
}))

const StyledCard = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.customtext.black20}`,
    padding: theme.spacing(4),
    background: theme.palette.white,
    borderRadius: '12px',
}))
